/**
 * Abbildung fachlicher Werte auf Gestaltungsentscheidungen.
 *
 * Hier steht, welche Zustandsgruppe welchen Farbton bekommt und welche
 * Prioritaet welchen. Das steht **nicht** in den Komponenten: Die Zuordnung
 * gilt in Liste, Einzelansicht und spaeter im Portal gleichermassen, und drei
 * Stellen mit derselben Entscheidung sind zwei zu viel.
 *
 * Die Farbe aus `ticket_priorities.color` wird bewusst nicht unmittelbar
 * gesetzt. Sie ist ein heller Farbwert und braeche im Dunkelmodus; die
 * Oberflaeche bildet sie auf einen Ton ab und ueberlaesst den konkreten Wert
 * den Token.
 */

import type { BadgeTone } from '../../components/Badge';
import type { StateCategory } from './api';

export function toneForStateCategory(category: StateCategory): BadgeTone {
  switch (category) {
    case 'new':
      return 'info';
    case 'open':
      return 'info';
    case 'pending_customer':
    case 'pending_internal':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'neutral';
  }
}

/** Stufe 1 ist die hoechste Prioritaet -- siehe `ticket_priorities.level`. */
export function toneForPriorityLevel(level: number): BadgeTone {
  if (level <= 1) return 'danger';
  if (level === 2) return 'warning';
  if (level === 3) return 'info';
  return 'neutral';
}

/**
 * Die Zustandsgruppen in der Reihenfolge, in der sie im Filter erscheinen.
 *
 * Nicht alphabetisch, sondern entlang des Lebenszyklus -- so, wie ein
 * Bearbeiter sie im Kopf hat.
 */
export const STATE_CATEGORIES: readonly StateCategory[] = [
  'new',
  'open',
  'pending_customer',
  'pending_internal',
  'resolved',
  'closed',
];

/**
 * Formt den Kategoriebaum in eine flache Auswahlliste.
 *
 * Ein Auswahlfeld kann keine Hierarchie darstellen; die Einrueckung uebernimmt
 * darum das Beschriftungspraefix. Ein Aufklappbaum waere in einer Maske, die
 * man in wenigen Sekunden ausfuellen soll, mehr Umstand als Hilfe -- bei
 * hoechstens drei Ebenen bleibt die Liste ueberschaubar.
 */
export function flattenCategories(
  nodes: readonly CategoryNode[],
  depth = 0,
): { id: string; label: string }[] {
  return nodes.flatMap((node) => [
    {
      id: node.id,
      label: `${'\u00a0\u00a0'.repeat(depth)}${depth > 0 ? '\u2514 ' : ''}${node.name}`,
    },
    ...flattenCategories(node.children ?? [], depth + 1),
  ]);
}

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}
